// src/components/ErrorBoundary.js
// GR33D-SIMPLIFIED-V2: [2025-02-27] - Gestion d'erreur simplifiée

import React from 'react';
import { ERROR_MESSAGES } from '../constants/contract';
import Logger from '../utils/logger-adapter';

/**
 * Composant de capture et affichage des erreurs simplifié
 * Fournit une interface utilisateur en cas d'erreur et des mécanismes de récupération
 */
class ErrorBoundary extends React.Component {
    constructor(props) {
        super(props);
        this.state = { 
            hasError: false, 
            error: null,
            errorInfo: null,
            retryCount: 0
        };
    }

    // Capture les erreurs pendant le rendu
    static getDerivedStateFromError(error) {
        return { 
            hasError: true, 
            error 
        };
    }

    // Enregistre les détails de l'erreur
    componentDidCatch(error, errorInfo) {
        // Log de l'erreur
        Logger.error(error, { 
            context: 'ErrorBoundary',
            component: errorInfo?.componentStack
        });

        // Classification simple de l'erreur
        const errorType = this.categorizeError(error);
        
        // Tentative de résolution pour les erreurs réseau
        if (errorType === 'NETWORK' && window.ethereum) {
            this.handleNetworkError();
        }

        // Mise à jour du compteur de tentatives
        this.setState(prevState => ({ 
            errorInfo,
            retryCount: prevState.retryCount + 1
        }));
    }

    // Classification simple des erreurs courantes
    categorizeError(error) {
        const message = error.message || '';
        
        if (message.includes('network') || message.includes('chain')) {
            return 'NETWORK';
        }
        if (message.includes('rejected') || error.code === 4001) {
            return 'USER_REJECTED';
        }
        if (message.includes('execution reverted')) {
            return 'CONTRACT';
        }
        if (message.includes('blacklist')) {
            return 'BLACKLIST';
        }
        
        return 'UNKNOWN';
    }

    // Gestion des erreurs réseau
    async handleNetworkError() {
        try {
            await window.ethereum.request({
                method: 'wallet_switchEthereumChain',
                params: [{ chainId: '0x1' }] // Ethereum Mainnet
            });
            
            // Reset state après correction
            this.setState({
                hasError: false,
                error: null,
                errorInfo: null
            });
        } catch (switchError) {
            Logger.error(switchError, { context: 'networkSwitch' });
        }
    }

    // Formatage convivial des messages d'erreur
    formatErrorMessage(error) {
        if (!error) return 'An unexpected error occurred';

        const errorType = this.categorizeError(error);
        const message = error.message || error.toString();
        
        switch (errorType) {
            case 'NETWORK':
                return ERROR_MESSAGES.UNSUPPORTED_NETWORK;
            case 'USER_REJECTED':
                return ERROR_MESSAGES.WALLET_DISCONNECTED;
            case 'CONTRACT':
                if (message.includes('Trading not enabled')) {
                    return ERROR_MESSAGES.TRADING_DISABLED;
                }
                if (message.includes('Max burn reached')) {
                    return ERROR_MESSAGES.MAX_BURN_REACHED;
                }
                if (message.includes('Exceeds limit')) {
                    return ERROR_MESSAGES.EXCEEDS_TX_LIMIT;
                }
                return 'Smart contract error. Please try again.';
            case 'BLACKLIST':
                return ERROR_MESSAGES.ADDRESS_BLACKLISTED;
            default:
                return message;
        }
    }

    // Interface d'erreur simplifiée
    render() {
        if (this.state.hasError) {
            return (
                <div 
                    style={{ 
                        position: 'fixed',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        background: 'rgba(0, 0, 0, 0.9)',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        padding: '20px',
                        color: '#fff',
                        zIndex: 9999
                    }}
                >
                    <div
                        style={{
                            maxWidth: '600px',
                            background: 'rgba(0, 0, 0, 0.8)',
                            padding: '2rem',
                            borderRadius: '12px',
                            border: '1px solid rgba(255, 179, 71, 0.3)',
                            textAlign: 'center'
                        }}
                    >
                        <h2 style={{ color: '#ffb347', marginBottom: '1rem' }}>
                            Application Error
                        </h2>
                        <p style={{ marginBottom: '1rem' }}>
                            {this.formatErrorMessage(this.state.error)}
                        </p>
                        
                        {this.state.retryCount > 0 && (
                            <p style={{ color: '#ff9f1c', fontSize: '0.9rem', marginBottom: '1rem' }}>
                                Retry attempt: {this.state.retryCount}/3
                            </p>
                        )}
                        
                        {process.env.NODE_ENV === 'development' && this.state.errorInfo && (
                            <details 
                                style={{ 
                                    whiteSpace: 'pre-wrap',
                                    marginTop: '1rem',
                                    padding: '1rem',
                                    background: 'rgba(0, 0, 0, 0.3)',
                                    borderRadius: '8px',
                                    textAlign: 'left'
                                }}
                            >
                                <summary style={{ cursor: 'pointer', color: '#ffb347' }}>
                                    Error Details
                                </summary>
                                <pre style={{ 
                                    marginTop: '1rem',
                                    fontSize: '0.9rem',
                                    overflow: 'auto',
                                    maxHeight: '200px'
                                }}>
                                    {this.state.error && this.state.error.toString()}
                                    <br />
                                    {this.state.errorInfo.componentStack}
                                </pre>
                            </details>
                        )}

                        <button
                            onClick={() => window.location.reload()}
                            style={{
                                marginTop: '2rem',
                                padding: '0.8rem 2rem',
                                background: '#ffb347',
                                color: '#000',
                                border: 'none',
                                borderRadius: '8px',
                                cursor: 'pointer',
                                fontWeight: 'bold'
                            }}
                        >
                            Reload Application
                        </button>
                    </div>
                </div>
            );
        }

        return this.props.children;
    }
}

export default ErrorBoundary;
